import {Fragment, memo, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Comment from './Comment';
import CommentDivider from './CommentDivider';
import CommentInput from './CommentInput';
import {pAlert} from '../../common/component/PAlert';
import TextButton from '../../common/component/TextButton';
import {showAuthModal} from '../../common/function';
import useLogin from '../../common/hook/useLogin';
import {ButtonSet} from '../../common/type/alert';
import {CommentThemeContext} from '../common';
import useCommentEvent from '../hook/useCommentEvent';
import {actions} from '../state/action';
import {IComment, ICommentLikeStatus} from '../type';

interface IProps {
  comment: IComment;
  contentId?: number;
  isOpen: boolean;
  replyComments: IComment[];
  isEnd: boolean;
  openReportModal: (id: number) => void;
  fetchReplyComments: (parentId: number, isFirst?: boolean) => void;
  postReplyComment?: (parentId: number, content: string) => void;
  deleteComment: (id: number) => void;
  changeLikeStatus: (id: number, likeStatus: ICommentLikeStatus) => void;
  toggleReplyComments: (id: number, isOpen: boolean) => void;
}

function CommentSection({
  comment,
  contentId,
  isOpen,
  replyComments,
  isEnd,
  openReportModal,
  fetchReplyComments,
  postReplyComment,
  deleteComment,
  changeLikeStatus,
  toggleReplyComments,
}: IProps) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {
    replyCommentBg,
    replyMoreTextStyle,
    replyInputVariant,
    replyDividerColor,
  } = useContext(CommentThemeContext);
  const {isLogin} = useLogin();

  const {sendCommentEvent, sendCommentViewImp} = useCommentEvent();

  const onClickReply = (id: number) => {
    if (isOpen) {
      toggleReplyComments(id, !isOpen);
    } else {
      // TODO: 로딩처리
      fetchReplyComments(id, true);
    }

    sendCommentEvent({
      actionName: 'comment_reply',
      action: 'click',
      customProps: {comment_id: id},
    });
  };

  const onClickDelete = async (id: number) => {
    sendCommentEvent({
      actionName: 'delete',
      action: 'click',
      customProps: {comment_id: id},
    });

    const {okClicked} = await pAlert({
      buttonSet: ButtonSet.CancelAndConfirm,
      title: t => t('comment_button_delete_popup'),
      labelConfirm: t => t('common_ok'),
    });

    // TODO: 로딩 처리
    okClicked && deleteComment(id);
  };

  const onClickReplyPost = (content: string) => {
    postReplyComment?.(comment.id, content);
  };

  const onClickMore = () => {
    fetchReplyComments(comment.id);
    sendCommentEvent({
      actionName: 'more_reply',
      action: 'click',
      customProps: {comment_id: comment.id},
    });
  };

  const onClickReport = (id: number) => {
    sendCommentEvent({
      actionName: 'report',
      action: 'click',
      customProps: {comment_id: id},
    });
    openReportModal(id);
  };

  const onClickBlock = async ({id, userName}: IComment) => {
    if (!isLogin) {
      showAuthModal();
      return;
    }

    sendCommentViewImp({
      customProps: {comment_id: comment.id, popup_type: 'comment_block'},
    });
    const {okClicked} = await pAlert({
      title: t('comment_block_popup_title', {nickname: userName}),
      desc: t('comment_block_popup_content'),
      buttonSet: ButtonSet.CancelAndConfirm,
    });

    sendCommentEvent({
      actionName: okClicked ? 'popup_ok' : 'popup_cancel',
      action: 'click',
      customProps: {comment_id: comment.id, popup_type: 'comment_block'},
    });
    if (okClicked) {
      dispatch(actions.fetchBlockWriter(id, contentId));
    }
  };

  const onClickUnblock = async ({id, userName}: IComment) => {
    if (!isLogin) {
      showAuthModal();
      return;
    }

    sendCommentViewImp({
      customProps: {comment_id: comment.id, popup_type: 'comment_unblock'},
    });
    const {okClicked} = await pAlert({
      title: t('comment_unblock_popup_title', {nickname: userName}),
      desc: t('comment_unblock_popup_content'),
      buttonSet: ButtonSet.CancelAndConfirm,
    });

    sendCommentEvent({
      actionName: okClicked ? 'popup_ok' : 'popup_cancel',
      action: 'click',
      customProps: {comment_id: comment.id, popup_type: 'comment_unblock'},
    });
    if (okClicked) {
      dispatch(actions.fetchUnBlockWriter(id, contentId));
    }
  };

  const canWriteReply = !!postReplyComment;
  return (
    <>
      <Comment
        comment={comment}
        showReplyBtn={!!comment.replyCount || canWriteReply}
        isReplyOpen={isOpen}
        onChangeLikeStatus={changeLikeStatus}
        onClickReply={onClickReply}
        onClickReport={onClickReport}
        onClickDelete={onClickDelete}
        onClickBlock={onClickBlock}
        onClickUnblock={onClickUnblock}
      />
      {isOpen && (
        <div
          className={cn(
            'px-18 -ml-18 w-[100vw] md:w-[490px] lg:w-[630px]',
            replyCommentBg
          )}
        >
          <ul>
            {replyComments.map((reply, index) => (
              <Fragment key={reply.id}>
                {index > 0 && <CommentDivider lineColor={replyDividerColor} />}
                <li>
                  <Comment
                    comment={reply}
                    onChangeLikeStatus={changeLikeStatus}
                    onClickReport={onClickReport}
                    onClickDelete={onClickDelete}
                    onClickBlock={onClickBlock}
                    onClickUnblock={onClickUnblock}
                  />
                </li>
              </Fragment>
            ))}
          </ul>
          {(canWriteReply || !isEnd) && (
            <div
              className={cn('text-center', {
                'pt-10': canWriteReply && replyComments.length,
                'pt-30': canWriteReply && !replyComments.length,
                'pb-30': isEnd,
                'z-1': canWriteReply,
              })}
            >
              {canWriteReply && (
                <CommentInput
                  isReply={true}
                  variant={replyInputVariant}
                  placeholder={t('comment_reply_before_enter')}
                  onClickPost={onClickReplyPost}
                />
              )}
              {!isEnd && (
                <TextButton
                  className={cn('btn-transparent my-24', replyMoreTextStyle)}
                  onClick={onClickMore}
                >
                  {t('comment_reply_more')}
                </TextButton>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default memo(CommentSection);
